'use client';
import { OrderSide } from '@polymarket/client';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/hooks/useAuth';
import { useComboClient } from '@/hooks/useComboClient';
import type {
  ComboActivityItem,
  ComboListResponse,
  ComboMarket,
  ComboPosition,
  ComboQuote,
  ComboQuoteRequest,
} from '@/types';
import { combosApi } from '../api/combos.api';

const COMBO_MARKETS_PAGE_LIMIT = 20;

export const COMBOS_QUERY_KEYS = {
  all: ['combos'] as const,
  markets: () => [...COMBOS_QUERY_KEYS.all, 'markets'] as const,
  rfqStatus: (rfqId: string) => [...COMBOS_QUERY_KEYS.all, 'rfq-status', rfqId] as const,
  positions: () => [...COMBOS_QUERY_KEYS.all, 'positions'] as const,
  activity: () => [...COMBOS_QUERY_KEYS.all, 'activity'] as const,
};

/** Browse combo-eligible markets (public, no auth gate). */
export const useComboMarketsInfinite = () => {
  return useInfiniteQuery<{ markets: ComboMarket[]; nextCursor?: string }>({
    queryKey: COMBOS_QUERY_KEYS.markets(),
    queryFn: async ({ pageParam }) => {
      const response = await combosApi.listMarkets({
        nextCursor: pageParam as string | undefined,
        limit: COMBO_MARKETS_PAGE_LIMIT,
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined as string | undefined,
  });
};

/**
 * Combo-eligible markets keyed by conditionId, for cross-referencing against the
 * general markets feed (which has no notion of combo eligibility on its own).
 * Fetches every page up front since the feed needs a synchronous eligibility
 * check per card, not paginated browsing.
 */
/**
 * @param enabled Only fetch while combo-select mode is actually active — this
 * paginates up to 20 requests (2000 markets) and was previously firing
 * unconditionally on every Home/Trending page load regardless of whether the
 * user ever touches the combos feature.
 */
export const useComboEligibleMarketsMap = (enabled: boolean = true) => {
  return useQuery<Map<string, ComboMarket>>({
    queryKey: [...COMBOS_QUERY_KEYS.markets(), 'eligible-map'],
    queryFn: async () => {
      const map = new Map<string, ComboMarket>();
      let cursor: string | undefined;
      let pagesFetched = 0;
      const MAX_PAGES = 20; // safety cap: 20 * 100 = 2000 markets

      do {
        const response = await combosApi.listMarkets({ nextCursor: cursor, limit: 100 });
        response.data.markets.forEach((market) => map.set(market.conditionId, market));
        cursor = response.data.nextCursor;
        pagesFetched += 1;
      } while (cursor && pagesFetched < MAX_PAGES);

      return map;
    },
    staleTime: 60_000,
    enabled,
  });
};

/**
 * Fetch a working window of the combo catalog as a flat array, for the
 * redesigned builder that groups markets into category tabs client-side.
 *
 * The catalog is volume-sorted (politics dominates the top; sports sit deeper),
 * so we page several times to surface a representative spread of categories.
 * This is a bounded window, not the whole catalog — enough to populate the
 * builder without fetching thousands of markets on tab open.
 */
export const useComboCatalog = (maxPages = 6, pageSize = 100) => {
  return useQuery<ComboMarket[]>({
    queryKey: [...COMBOS_QUERY_KEYS.markets(), 'catalog', maxPages, pageSize],
    queryFn: async () => {
      const markets: ComboMarket[] = [];
      let cursor: string | undefined;
      let pagesFetched = 0;

      do {
        const response = await combosApi.listMarkets({ nextCursor: cursor, limit: pageSize });
        markets.push(...response.data.markets);
        cursor = response.data.nextCursor;
        pagesFetched += 1;
      } while (cursor && pagesFetched < maxPages);

      return markets;
    },
    staleTime: 60_000,
  });
};

/**
 * Request a quote for a set of combo legs.
 *
 * Non-custodial: runs on a client-side SecureClient (from useComboClient) so the
 * user's own wallet is the signer. The BE can't sign for user-owned deposit
 * wallets, so quote/accept happen here, not via the backend. Results are wrapped
 * as `{ data }` to preserve the shape the ComboBuilder already consumes.
 */
export const useRequestComboQuote = () => {
  const client = useComboClient();
  return useMutation({
    mutationFn: async (payload: ComboQuoteRequest) => {
      if (!client) {
        throw new Error('Trading client not ready. Complete wallet setup and try again.');
      }
      const result = await client.requestComboQuote({
        legPositionIds: payload.legPositionIds,
        direction: payload.direction === 'SELL' ? OrderSide.SELL : OrderSide.BUY,
        ...(payload.direction === 'SELL' ? { size: payload.size } : { amount: payload.amount }),
      } as never);
      return {
        data: {
          rfqId: result.rfqId,
          quote: (result.quote ?? null) as ComboQuote | null,
          reason: result.quote ? undefined : (result as { reason?: string }).reason,
        },
      };
    },
  });
};

/** Accept a previously requested combo quote (client-side signing). */
export const useAcceptComboQuote = () => {
  const client = useComboClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ quote }: { rfqId: string; quote: ComboQuote }) => {
      if (!client) {
        throw new Error('Trading client not ready. Complete wallet setup and try again.');
      }
      const result = await client.acceptComboQuote(quote as never);
      return {
        data: {
          status: result.status,
          rfqId: result.rfqId,
          reason: (result as { reason?: string }).reason,
        },
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMBOS_QUERY_KEYS.positions() });
      queryClient.invalidateQueries({ queryKey: COMBOS_QUERY_KEYS.activity() });
    },
  });
};

/**
 * Track fill status for an accepted RFQ until it reaches a terminal state.
 * `waitForComboFill` blocks until the RFQ fills/fails/expires (or times out), so
 * we let react-query retry across timeout windows rather than manual polling.
 */
export const useComboRfqStatus = (rfqId?: string) => {
  const client = useComboClient();
  return useQuery({
    queryKey: COMBOS_QUERY_KEYS.rfqStatus(rfqId || ''),
    queryFn: async () => {
      if (!client || !rfqId) throw new Error('Trading client not ready.');
      const result = await client.waitForComboFill({ rfqId, timeoutMs: 60_000 });
      return {
        data: { status: result.status, txHash: (result as { txHash?: string }).txHash },
      };
    },
    enabled: !!rfqId && !!client,
    // A timeout means "still executing" — keep waiting across further windows.
    retry: 10,
    retryDelay: 1000,
  });
};

const COMBO_LIST_PAGE_SIZE = 20;

/**
 * The user's on-chain combo positions, read CLIENT-SIDE via the combo
 * SecureClient (the BE can't sign for the user's deposit wallet). Returns real
 * positions (shares, entry price, resolution status), not RFQ order history.
 */
export const useInfiniteComboPositions = () => {
  const { user } = useAuth();
  const client = useComboClient();

  return useInfiniteQuery<ComboListResponse<ComboPosition>>({
    queryKey: [...COMBOS_QUERY_KEYS.positions(), user?.proxyWalletAddress],
    queryFn: async ({ pageParam }) => {
      if (!client) throw new Error('Trading client not ready.');
      const page = await client
        .listComboPositions({
          // The SDK cursor is a branded type; the opaque page-param string is
          // passed straight through at runtime.
          cursor: pageParam as unknown as undefined,
          pageSize: COMBO_LIST_PAGE_SIZE,
        })
        .firstPage();
      return {
        items: page.items as unknown as ComboPosition[],
        nextCursor: page.nextCursor,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined as string | undefined,
    enabled: !!client,
  });
};

/** The user's combo lifecycle activity, read client-side via the combo client. */
export const useInfiniteComboActivity = () => {
  const { user } = useAuth();
  const client = useComboClient();

  return useInfiniteQuery<ComboListResponse<ComboActivityItem>>({
    queryKey: [...COMBOS_QUERY_KEYS.activity(), user?.proxyWalletAddress],
    queryFn: async ({ pageParam }) => {
      if (!client) throw new Error('Trading client not ready.');
      const page = await client
        .listComboActivity({
          cursor: pageParam as unknown as undefined,
          pageSize: COMBO_LIST_PAGE_SIZE,
        })
        .firstPage();
      return {
        items: page.items as unknown as ComboActivityItem[],
        nextCursor: page.nextCursor,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined as string | undefined,
    enabled: !!client,
  });
};
